import React, { useCallback, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { IonGrid, IonIcon, IonItem, IonLabel, IonText } from '@ionic/react';
import { chevronForwardOutline, languageOutline } from 'ionicons/icons';
import { useAppLanguage } from '../hooks/useAppLanguage';
import PortalDropdown from './PortalDropdown';
import styles from './LanguageSetting.module.scss';
import { useTranslation } from 'react-i18next';

// i18n-ignore: labels are endonyms so they stay readable regardless of the active language.
const LANGUAGE_OPTIONS = [
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Español' },
  { code: 'pt', label: 'Português' },
];

interface LanguageSettingProps {
  itemClassName?: string;
  badgeClassName?: string;
  /** Custom trigger (e.g. desktop's settingRow). Defaults to an IonItem row. */
  renderTrigger?: (props: {
    currentLabel: string;
    toggle: () => void;
  }) => React.ReactNode;
}

const LanguageSetting: React.FC<LanguageSettingProps> = ({ itemClassName, badgeClassName, renderTrigger }) => {
  const { t } = useTranslation();
  const { language, setLanguage } = useAppLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const anchorRef = useRef<HTMLIonGridElement>(null);
  const portalRef = useRef<HTMLIonGridElement>(null);
  const close = useCallback(() => setIsOpen(false), []);

  const current = LANGUAGE_OPTIONS.find((o) => o.code === language) ?? LANGUAGE_OPTIONS[0];
  const toggle = () => setIsOpen((v) => !v);

  return (
    <>
      <IonGrid ref={anchorRef} className={styles.anchor}>
        {renderTrigger ? renderTrigger({ currentLabel: current.label, toggle }) : (
          <IonItem
            button
            detail
            detailIcon={chevronForwardOutline}
            lines="none"
            className={itemClassName}
            onClick={toggle}
          >
            <IonText slot="start" className={badgeClassName}>
              <IonIcon icon={languageOutline} />
            </IonText>
            <IonLabel>
              <h3>{t('components.LanguageSetting.language')}</h3>
              <p>{current.label}</p>
            </IonLabel>
          </IonItem>
        )}
      </IonGrid>
      {/* Portaled to <body> so the dropdown isn't clipped or re-anchored by the
          settings card's overflow/backdrop-filter containing block. */}
      {createPortal(<IonGrid ref={portalRef} className={styles.portalRoot} />, document.body)}
      <PortalDropdown
        isOpen={isOpen}
        anchorRef={anchorRef}
        portalRef={portalRef as unknown as React.RefObject<HTMLDivElement | null>}
        onClose={close}
        options={LANGUAGE_OPTIONS.map((o) => ({
          label: o.label,
          value: o.code,
          selected: o.code === language,
        }))}
        onSelect={setLanguage}
      />
    </>
  );
};

export default LanguageSetting;
